/**
 * TaskFieldSelector
 *
 * Autonomous component for selecting task fields
 * - Loads data from Firestore independently
 * - Uses rc-tree for tree display
 * - Supports path format: @task[ID].field[index]
 * - Optimizes paths: if all children selected, saves only parent path
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, CircularProgress, Typography, LinearProgress } from '@mui/material';
import Tree from 'rc-tree';
import 'rc-tree/assets/index.css';
import type { Key } from 'rc-tree/lib/interface';

import { TaskService } from '@modules/firebase-core/services/TaskService';
import { taskConverter } from '@modules/firebase-core/converters/taskConverter';
import type { Task } from '@segmently/schema-models/entities';

import type { TaskFieldSelectorProps, TaskTreeNode } from './types';
import { taskToTreeNode } from './taskToTreeNode';
import { treeNodeIdsToPaths, pathsToTreeNodeIds, deduplicatePaths } from './pathConverter';
import { pathsToJson } from './pathsToJson';

/**
 * TaskFieldSelector Component
 */
export const TaskFieldSelector: React.FC<TaskFieldSelectorProps> = ({
  projectId,
  value,
  onChange,
  height = 400,
  selectAll = false,
  onHydratedDataChange,
  allowedTaskIds,
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0, currentItem: '' });

  // Load tasks from Firestore
  useEffect(() => {
    let isMounted = true;

    const loadTasks = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadProgress({ loaded: 0, total: 0, currentItem: 'Loading tasks...' });

        const taskService = new TaskService(projectId, taskConverter);

        // Load all tasks
        const loadedTasks = await taskService.list();

        if (isMounted) {
          setTasks(loadedTasks);
          setLoadProgress({
            loaded: loadedTasks.length,
            total: loadedTasks.length,
            currentItem: 'Complete',
          });
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load tasks');
          console.error('Failed to load tasks:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadTasks();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  // Phase F: when allowedTaskIds is provided, only those tasks are shown/selectable.
  const visibleTasks = useMemo(
    () => (allowedTaskIds ? tasks.filter((t) => allowedTaskIds.includes(t.id!)) : tasks),
    [tasks, allowedTaskIds],
  );

  // Prune selected task paths that fall outside the allowed set when it changes.
  useEffect(() => {
    if (!allowedTaskIds) return;
    const allowed = new Set(allowedTaskIds);
    const pruned = value.filter((p) => {
      const m = p.match(/^@task\[([^\]]+)\]/);
      return !m || allowed.has(m[1]);
    });
    if (pruned.length !== value.length) onChange(pruned);
  }, [allowedTaskIds, value, onChange]);

  /**
   * The operator emptied the task selection ON PURPOSE.
   *
   * `selectAll` used to re-run on EVERY empty value, which made «uncheck all
   * routes» impossible: the boxes ticked themselves back within a frame, and
   * the deterministic 0-route state the readiness strip must be able to show
   * (strategy-intake-feasibility AC-006) was unreachable from the UI. The flag
   * mirrors `VariableFieldSelector`'s `preselectedRef`: preselection happens
   * once per fresh (external, empty) reset, and any non-empty value re-arms it.
   */
  const clearedByUserRef = useRef(false);
  useEffect(() => {
    if (value.length > 0) clearedByUserRef.current = false;
  }, [value.length]);

  /**
   * A NEW allowed task set is a new question, not the old one re-asked: pinning
   * a different insight analysis must preselect ITS routes even when the
   * operator had emptied the previous analysis's set (design brief «Pick an
   * analysis → … Ready to generate · 3 routes»). Declared BEFORE the selectAll
   * effect so the re-arm is visible to it in the same commit.
   */
  const allowedTaskIdsKey = (allowedTaskIds ?? []).join(',');
  const lastAllowedTaskIdsKeyRef = useRef(allowedTaskIdsKey);
  useEffect(() => {
    if (lastAllowedTaskIdsKeyRef.current === allowedTaskIdsKey) return;
    lastAllowedTaskIdsKeyRef.current = allowedTaskIdsKey;
    clearedByUserRef.current = false;
  }, [allowedTaskIdsKey]);

  // Handle selectAll: auto-select all VISIBLE tasks after loading
  useEffect(() => {
    if (clearedByUserRef.current) return;
    if (selectAll && visibleTasks.length > 0 && value.length === 0 && !loading) {
      const allTaskPaths = visibleTasks.map((task) => `@task[${task.id}]`);
      onChange(allTaskPaths);
    }
  }, [selectAll, visibleTasks, value.length, loading, onChange]);

  // Generate hydrated data when value or tasks change
  useEffect(() => {
    if (onHydratedDataChange && tasks.length > 0) {
      if (value.length > 0) {
        const hydratedData = pathsToJson(value, tasks);
        onHydratedDataChange(hydratedData);
      } else {
        // Clear hydrated data when selection is empty
        onHydratedDataChange([]);
      }
    }
  }, [value, tasks, onHydratedDataChange]);

  // Convert tasks to tree data (Phase F: only the visible/allowed tasks)
  const treeData = useMemo<TaskTreeNode[]>(() => {
    return visibleTasks.map((task) => taskToTreeNode(task));
  }, [visibleTasks]);

  // Convert value (paths) to checkedKeys (node keys)
  const checkedKeys = useMemo<Key[]>(() => {
    if (!value || value.length === 0) return [];
    return pathsToTreeNodeIds(value, treeData);
  }, [value, treeData]);

  // Handle selection changes
  const handleCheck = (checkedKeysValue: Key[] | { checked: Key[]; halfChecked: Key[] }) => {
    // rc-tree can return either array or object with checked/halfChecked
    const checkedArray = Array.isArray(checkedKeysValue)
      ? checkedKeysValue
      : checkedKeysValue.checked;

    const stringKeys = checkedArray.map((key) => String(key));

    // Convert node keys back to paths
    const paths = treeNodeIdsToPaths(stringKeys, treeData);

    // Smart deduplication: removes child paths ONLY if ALL children are selected
    // This optimizes outgoing data (e.g., ["@task[ID].field1", "@task[ID].field2", ...] → ["@task[ID]"])
    const dedupedPaths = deduplicatePaths(paths, treeData);

    // An explicit emptying must STICK — see `clearedByUserRef`.
    clearedByUserRef.current = dedupedPaths.length === 0;

    onChange(dedupedPaths);
  };

  // Loading state
  if (loading) {
    const progressPercent =
      loadProgress.total > 0 ? Math.round((loadProgress.loaded / loadProgress.total) * 100) : 0;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          flexDirection: 'column',
          gap: 2,
          px: 4,
        }}
      >
        <CircularProgress size={40} />

        <Box sx={{ width: '100%', maxWidth: 400 }}>
          <LinearProgress
            variant={loadProgress.total > 0 ? 'determinate' : 'indeterminate'}
            value={progressPercent}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {loadProgress.currentItem || 'Loading tasks...'}
          </Typography>
          {loadProgress.total > 0 && (
            <Typography variant="caption" color="text.secondary">
              {loadProgress.loaded} / {loadProgress.total} ({progressPercent}%)
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Typography variant="body1" color="error">
          Error loading data
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  // Empty state
  if (visibleTasks.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {allowedTaskIds && tasks.length > 0 ? 'Select an insight analysis above to choose its tasks' : 'No tasks found'}
        </Typography>
      </Box>
    );
  }

  // Display tree
  return (
    <Box
      sx={{
        height,
        overflow: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Tree
        treeData={treeData}
        checkable
        checkedKeys={checkedKeys}
        onCheck={handleCheck}
        defaultExpandAll={false}
        showIcon={false}
      />
    </Box>
  );
};
